import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountingSyncApi } from '../services/accountingSyncApi';
import { creditNoteKeys } from './useCreditNotes';
import { paymentKeys } from './usePayments';
import { workOrderKeys } from './useWorkOrders';
import type { UpdateAccountingSyncSettingsRequest } from '../types/service.types';

export type UnsyncedKind = 'work-orders' | 'payments' | 'credit-notes';

export const ACCOUNTING_SYNC_PER_PAGE = 15;

export const accountingSyncKeys = {
  all: ['accounting-sync'] as const,
  summary: () => [...accountingSyncKeys.all, 'summary'] as const,
  list: (kind: UnsyncedKind, page: number) => [...accountingSyncKeys.all, 'list', kind, page] as const,
  batches: (page: number) => [...accountingSyncKeys.all, 'batches', page] as const,
  unlocks: (page: number) => [...accountingSyncKeys.all, 'unlocks', page] as const,
  settings: () => [...accountingSyncKeys.all, 'settings'] as const,
};

interface EnabledOption {
  enabled?: boolean;
}

export function useAccountingSyncSummary({ enabled = true }: EnabledOption = {}) {
  return useQuery({
    queryKey: accountingSyncKeys.summary(),
    queryFn: () => accountingSyncApi.summary(),
    enabled,
  });
}

export function useUnsyncedList(kind: UnsyncedKind, page: number, { enabled = true }: EnabledOption = {}) {
  return useQuery({
    queryKey: accountingSyncKeys.list(kind, page),
    queryFn: () => {
      switch (kind) {
        case 'work-orders':
          return accountingSyncApi.workOrders(page, ACCOUNTING_SYNC_PER_PAGE);
        case 'payments':
          return accountingSyncApi.payments(page, ACCOUNTING_SYNC_PER_PAGE);
        case 'credit-notes':
          return accountingSyncApi.creditNotes(page, ACCOUNTING_SYNC_PER_PAGE);
      }
    },
    enabled,
  });
}

export function useAccountingSyncBatches(page: number, { enabled = true }: EnabledOption = {}) {
  return useQuery({
    queryKey: accountingSyncKeys.batches(page),
    queryFn: () => accountingSyncApi.batches(page, ACCOUNTING_SYNC_PER_PAGE),
    enabled,
  });
}

export function useUnlockLog(page: number, { enabled = true }: EnabledOption = {}) {
  return useQuery({
    queryKey: accountingSyncKeys.unlocks(page),
    queryFn: () => accountingSyncApi.unlocks(page, ACCOUNTING_SYNC_PER_PAGE),
    enabled,
  });
}

export function useAccountingSyncSettings({ enabled = true }: EnabledOption = {}) {
  return useQuery({
    queryKey: accountingSyncKeys.settings(),
    queryFn: () => accountingSyncApi.settings(),
    enabled,
  });
}

export function useExportAccountingBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => accountingSyncApi.exportBatch(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      void queryClient.invalidateQueries({ queryKey: creditNoteKeys.all });
    },
    // 15-REVIEW WR-04 — the sync page's own caches refresh on a 409 too
    // (ACCOUNTING_BATCH_EMPTY: another user exported first), so the counts
    // and the Export button never keep offering a dead action.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: accountingSyncKeys.all });
    },
  });
}

export function useUpdateAccountingSyncSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAccountingSyncSettingsRequest) => accountingSyncApi.updateSettings(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(accountingSyncKeys.settings(), settings);
      void queryClient.invalidateQueries({ queryKey: accountingSyncKeys.summary() });
    },
  });
}
